import dynamic from 'next/dynamic';
import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Portal } from '@air/primitive-portal';
import { useSysadmin } from '@air/sysadmin';

import { DevModalContext, type DevModalContextType } from '~/components/DevModal/DevModalContext';
import { useKonamiCode } from '~/hooks/useKonamiCode';
import { useIsAirEmail } from '~/swr-hooks/useIsAirEmail';

/**
 * Loaded lazily on purpose — do not turn this back into a static import.
 *
 * DevModal pulls in all of @air/sysadmin, and launchDarklyInterceptor imports useDevModal for its
 * dev flag overrides, so a static import puts the whole dev tools modal (628 of the _app graph's
 * 1,961 modules) into every production bundle. It opens by konami code or console helper, so
 * nothing needs it before first paint.
 */
const DevModal = dynamic(() => import('~/components/DevModal/DevModal').then((mod) => mod.DevModal), {
  ssr: false,
});

export type DevModalProviderProps = {
  children: ReactNode;
};

declare global {
  // biome-ignore lint/style/useConsistentTypeDefinitions: overriding window requires using an interface
  interface Window {
    __devModalOpen?: () => void;
    __devModalClose?: () => void;
    __checkDevModal?: () => {
      isDevModalOpen: boolean;
      isAirEmail: boolean | undefined;
      shouldShowDevButton: boolean | undefined;
    };
  }
}

/**
 * Provider component that manages the developer modal state and access control.
 *
 * Features:
 * - Only shows dev modal button to users with @air.inc email addresses
 * - Available in all environments for @air.inc users
 * - Supports both Konami code activation and button click
 * - Provides global debugging functions for console access
 *
 * Access Requirements:
 * 1. User must have @air.inc email address
 */
export const DevModalProvider = memo(({ children }: DevModalProviderProps) => {
  const [isDevModalOpen, setIsDevModalOpen] = useState(false);
  const { isAirEmail } = useIsAirEmail();
  const { isInternal } = useSysadmin();

  const shouldShowDevButton = isAirEmail || isInternal;

  // Dev flag override management
  const [devFlagOverrideKey, setDevFlagOverrideKey] = useState(0);
  const listenersRef = useRef<Array<() => void>>([]);

  /**
   * Handler for Konami code activation.
   * Opens dev modal if user has air.inc email (bypasses environment check for debugging).
   */
  const handleKonamiCode = useCallback(() => {
    if (isAirEmail) {
      setIsDevModalOpen(true);
    }
  }, [isAirEmail]);

  useKonamiCode(handleKonamiCode);

  /**
   * Closes the developer modal.
   */
  const handleCloseDevModal = useCallback(() => {
    setIsDevModalOpen(false);
  }, []);

  /**
   * Opens the developer modal via button click.
   * Requires air.inc email address.
   */
  const handleOpenDevModal = useCallback(() => {
    if (shouldShowDevButton) {
      setIsDevModalOpen(true);
    }
  }, [shouldShowDevButton]);

  /**
   * Setup global debugging functions for console access.
   * These functions allow developers to interact with the dev modal from the browser console.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    // Global function to force open the dev modal
    window.__devModalOpen = () => {
      setIsDevModalOpen(true);
    };

    // Global function to check dev modal state and permissions
    window.__checkDevModal = () => {
      return {
        isDevModalOpen,
        isAirEmail,
        shouldShowDevButton,
      };
    };

    return () => {
      delete window.__devModalOpen;
      delete window.__checkDevModal;
    };
  }, [isDevModalOpen, isAirEmail, shouldShowDevButton]);

  /**
   * Setup dev flag override event listeners.
   * Manages localStorage changes and notifies all registered listeners.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const notifyDevOverrideListeners = () => {
      setDevFlagOverrideKey((prev) => prev + 1);
      listenersRef.current.forEach((listener) => {
        listener();
      });
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'dev-flag-overrides') {
        notifyDevOverrideListeners();
      }
    };

    const handleDevFlagChange = () => {
      notifyDevOverrideListeners();
    };

    // Listen for storage changes from other tabs/windows
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom event from dev panel in same tab
    window.addEventListener('dev-flag-override-changed', handleDevFlagChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('dev-flag-override-changed', handleDevFlagChange);
    };
  }, []);

  /**
   * Allows feature flag hooks to register for dev flag override change notifications.
   * Returns an unregister function for cleanup.
   */
  const registerDevFlagListener = useCallback((callback: () => void) => {
    listenersRef.current.push(callback);

    // Return unregister function
    return () => {
      const index = listenersRef.current.indexOf(callback);

      if (index > -1) {
        listenersRef.current.splice(index, 1);
      }
    };
  }, []);

  const contextValue = useMemo<DevModalContextType>(
    () => ({
      isDevModalOpen,
      openDevModal: handleOpenDevModal,
      closeDevModal: handleCloseDevModal,
      shouldShowDevButton: shouldShowDevButton || false,
      devFlagOverrideKey,
      registerDevFlagListener,
    }),
    [
      isDevModalOpen,
      handleOpenDevModal,
      handleCloseDevModal,
      shouldShowDevButton,
      devFlagOverrideKey,
      registerDevFlagListener,
    ],
  );

  return (
    <DevModalContext.Provider value={contextValue}>
      {children}
      {isDevModalOpen && (
        <Portal>
          <DevModal isOpen={isDevModalOpen} onClose={handleCloseDevModal} />
        </Portal>
      )}
    </DevModalContext.Provider>
  );
});

DevModalProvider.displayName = 'DevModalProvider';
